import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { useTour } from './useTour.js';
import { computePosition, autoPlacement, autoUpdate, offset, shift, flip, } from '@floating-ui/dom';
import { ensureTourRoot, setSpotlightVisible, updateSpotlightRect, setMaskPointerEvents, } from '@digiwedge/tour-shadow';
import { events } from '@digiwedge/tour-core';
import { resolveTarget } from './resolveTarget.js';
const TABBABLE_SELECTOR = 'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const PREVIEW_TTL_MS = 30 * 60 * 1000;
function parsePreviewState() {
    try {
        const raw = sessionStorage.getItem('tour.preview');
        if (!raw)
            return { active: false, remaining: 0 };
        if (raw === '1')
            return { active: true, remaining: PREVIEW_TTL_MS };
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed.expiresAt !== 'number')
            return { active: false, remaining: 0 };
        if (parsed.origin && parsed.origin !== window.location.origin) {
            sessionStorage.removeItem('tour.preview');
            return { active: false, remaining: 0 };
        }
        const remaining = parsed.expiresAt - Date.now();
        if (remaining <= 0) {
            sessionStorage.removeItem('tour.preview');
            return { active: false, remaining: 0 };
        }
        return { active: true, remaining };
    }
    catch {
        return { active: false, remaining: 0 };
    }
}
function clampTimeout(ms) {
    const upper = 0x7fffffff;
    if (!Number.isFinite(ms))
        return upper;
    return Math.max(0, Math.min(ms, upper));
}
function getTabbables(root) {
    if (!root)
        return [];
    return Array.from(root.querySelectorAll(TABBABLE_SELECTOR)).filter((el) => {
        if (el.getAttribute('aria-hidden') === 'true')
            return false;
        if (el.hasAttribute('disabled'))
            return false;
        return el.tabIndex !== -1;
    });
}
function isPointWithinRect(rect, x, y) {
    return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
}
function eventPoint(ev) {
    if ('clientX' in ev && typeof ev.clientX === 'number') {
        return { x: ev.clientX, y: ev.clientY };
    }
    const touchEvent = ev;
    const touch = touchEvent.touches?.[0] ?? touchEvent.changedTouches?.[0];
    if (touch)
        return { x: touch.clientX, y: touch.clientY };
    return null;
}
function containsNode(root, node) {
    if (!root || !node)
        return false;
    if (root === node)
        return true;
    if ('contains' in root)
        return root.contains?.(node) ?? false;
    return false;
}
function useReposition(target, floating, placement, spotlightPadding) {
    useLayoutEffect(() => {
        if (!floating)
            return;
        if (!target) {
            Object.assign(floating.style, {
                position: 'fixed',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
            });
            return;
        }
        const middleware = [offset(12), shift({ padding: 16, crossAxis: true }), flip({ padding: 16 })];
        if (!placement || placement === 'auto')
            middleware.push(autoPlacement({ alignment: 'start' }));
        const compute = () => computePosition(target, floating, {
            middleware,
            placement: placement && placement !== 'auto' ? placement : undefined,
        })
            .then(({ x, y }) => {
            Object.assign(floating.style, {
                position: 'absolute',
                left: `${x}px`,
                top: `${y}px`,
                transform: 'translate3d(0,0,0)',
            });
            const rect = target.getBoundingClientRect();
            const pad = Math.max(0, spotlightPadding);
            updateSpotlightRect({
                x: rect.left - pad,
                y: rect.top - pad,
                width: rect.width + pad * 2,
                height: rect.height + pad * 2,
                radius: 8 + pad,
            });
        })
            .catch(() => undefined);
        compute();
        return autoUpdate(target, floating, compute, {
            ancestorResize: true,
            ancestorScroll: true,
            elementResize: true,
        });
    }, [target, floating, placement, spotlightPadding]);
}
export function TourOverlay() {
    const tour = useTour();
    const [step, setStep] = useState(null);
    const [index, setIndex] = useState(-1);
    const floatingRef = useRef(null);
    const containerRef = useRef(null);
    const prevFocus = useRef(null);
    const [previewBadge, setPreviewBadge] = useState(false);
    useEffect(() => {
        try {
            window.dispatchEvent(new CustomEvent('dw-tour-ready'));
        }
        catch {
            /* noop */
        }
    }, []);
    useEffect(() => {
        const syncState = () => {
            const s = tour.getState();
            if (s.active && s.index >= 0) {
                setIndex(s.index);
                setStep(s.steps[s.index] ?? null);
                ensureTourRoot();
                setSpotlightVisible(true);
            }
            else {
                setIndex(-1);
                setStep(null);
                setSpotlightVisible(false);
                setMaskPointerEvents(true);
            }
        };
        syncState();
        return tour.on(syncState);
    }, [tour]);
    useEffect(() => {
        // Measure first paint of overlay for perf telemetry
        try {
            const t0 = performance.now();
            const id = requestAnimationFrame(() => {
                const ms = Math.round(performance.now() - t0);
                const ev = new CustomEvent('dw_tour_overlay_first_paint', { detail: { ms } });
                window.dispatchEvent(ev);
            });
            return () => {
                try {
                    cancelAnimationFrame(id);
                }
                catch {
                    /* noop */
                }
            };
        }
        catch {
            /* noop */
        }
        return undefined;
    }, []);
    useEffect(() => {
        const onKey = (ev) => {
            if (!step)
                return;
            if (ev.key === 'Escape')
                tour.stop('skip');
            if (ev.key === 'ArrowRight')
                tour.next();
            if (ev.key === 'ArrowLeft')
                tour.back();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [step, tour]);
    // Detect preview mode via sessionStorage flag (set by runtime when launching a preview)
    useEffect(() => {
        const { active, remaining } = parsePreviewState();
        setPreviewBadge(active);
        if (!active || remaining <= 0)
            return;
        const id = window.setTimeout(() => {
            const next = parsePreviewState();
            setPreviewBadge(next.active);
        }, clampTimeout(remaining));
        return () => window.clearTimeout(id);
    }, []);
    const reportedMissing = useRef(new Set());
    const [targetEl, setTargetEl] = useState(null);
    useEffect(() => {
        if (!step) {
            setTargetEl(null);
            setSpotlightVisible(false);
            setMaskPointerEvents(true);
            return;
        }
        ensureTourRoot(typeof step.zIndex === 'number' ? { zIndex: step.zIndex } : {});
        setMaskPointerEvents(true);
        const resolvedDirect = step.target && document.body.contains(step.target) ? step.target : null;
        const resolved = resolvedDirect ?? (step.selector ? resolveTarget(step.selector) : null);
        if (resolved) {
            setTargetEl(resolved);
            setSpotlightVisible(true);
            return;
        }
        setTargetEl(null);
        setSpotlightVisible(false);
        if (!reportedMissing.current.has(step.id)) {
            const url = (() => {
                try {
                    return window.location.href;
                }
                catch {
                    return undefined;
                }
            })();
            events.emit({ type: 'target:missing', stepId: step.id, selector: step.selector, url });
            reportedMissing.current.add(step.id);
        }
        let cancelled = false;
        const poll = window.setInterval(() => {
            if (cancelled || !step.selector)
                return;
            const candidate = resolveTarget(step.selector);
            if (candidate && document.body.contains(candidate)) {
                setTargetEl(candidate);
                setSpotlightVisible(true);
                window.clearInterval(poll);
            }
        }, 240);
        return () => {
            cancelled = true;
            window.clearInterval(poll);
        };
    }, [step]);
    useReposition(targetEl, floatingRef.current, step?.placement, step?.spotlightPadding ?? 8);
    useEffect(() => {
        if (!step)
            return;
        const blockInteractions = !step.allowInteraction;
        const shouldClose = Boolean(step.closeOnOutsideClick);
        if (!blockInteractions && !shouldClose)
            return;
        const dialog = floatingRef.current;
        const listener = (ev) => {
            const node = (ev.composedPath?.()[0] ?? ev.target);
            if (containsNode(dialog, node))
                return;
            if (targetEl && containsNode(targetEl, node))
                return;
            if (targetEl) {
                const coords = eventPoint(ev);
                if (coords) {
                    const rect = targetEl.getBoundingClientRect();
                    if (isPointWithinRect(rect, coords.x, coords.y))
                        return;
                }
            }
            if (blockInteractions) {
                if ('preventDefault' in ev)
                    ev.preventDefault();
                if ('stopImmediatePropagation' in ev)
                    ev.stopImmediatePropagation();
                ev.stopPropagation();
            }
            if (shouldClose) {
                tour.stop('skip');
            }
        };
        const options = { capture: true };
        const eventsToTrap = [
            'pointerdown',
            'mousedown',
            'touchstart',
            'click',
        ];
        for (const name of eventsToTrap) {
            document.addEventListener(name, listener, options);
        }
        return () => {
            for (const name of eventsToTrap) {
                document.removeEventListener(name, listener, options);
            }
        };
    }, [step, targetEl, tour]);
    useEffect(() => {
        if (!step || step.allowInteraction)
            return;
        const body = document.body;
        const prev = typeof body.inert === 'boolean' ? body.inert : undefined;
        let toggled = false;
        try {
            if ('inert' in body) {
                body.inert = true;
                toggled = true;
            }
        }
        catch {
            toggled = false;
        }
        return () => {
            if (toggled) {
                body.inert = prev ?? false;
            }
        };
    }, [step]);
    const titleId = useId();
    const descriptionId = useId();
    useEffect(() => {
        if (!step)
            return;
        const dialog = floatingRef.current;
        if (!dialog)
            return;
        prevFocus.current = document.activeElement ?? null;
        const focusWithin = () => {
            const tabbables = getTabbables(dialog);
            (tabbables[0] ?? dialog).focus?.();
        };
        const onKeyDown = (ev) => {
            if (ev.key !== 'Tab')
                return;
            const tabbables = getTabbables(dialog);
            if (!tabbables.length) {
                ev.preventDefault();
                dialog.focus?.();
                return;
            }
            const active = document.activeElement;
            const currentIndex = active ? tabbables.indexOf(active) : -1;
            let nextIndex = currentIndex;
            if (ev.shiftKey) {
                nextIndex = currentIndex <= 0 ? tabbables.length - 1 : currentIndex - 1;
            }
            else {
                nextIndex =
                    currentIndex === tabbables.length - 1 || currentIndex === -1 ? 0 : currentIndex + 1;
            }
            ev.preventDefault();
            tabbables[nextIndex]?.focus?.();
        };
        const onFocusIn = (ev) => {
            if (!dialog.contains(ev.target)) {
                focusWithin();
            }
        };
        const frame = window.requestAnimationFrame(focusWithin);
        window.addEventListener('keydown', onKeyDown);
        window.addEventListener('focusin', onFocusIn);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('focusin', onFocusIn);
            window.cancelAnimationFrame(frame);
            prevFocus.current?.focus?.();
        };
    }, [step]);
    if (!step)
        return null;
    return (_jsxs("div", { ref: containerRef, style: { position: 'fixed', inset: 0, pointerEvents: 'none' }, "aria-live": "polite", children: [_jsx("div", { ref: floatingRef, style: {
                    pointerEvents: 'auto',
                    zIndex: 1,
                    maxWidth: 'min(360px, 90vw)',
                    fontFamily: 'system-ui',
                    outline: 'none',
                }, role: "dialog", tabIndex: -1, "aria-modal": "true", "aria-labelledby": step.title ? titleId : undefined, "aria-describedby": descriptionId, children: _jsxs("div", { style: panelStyle, children: [_jsx("div", { style: { display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }, children: _jsx("button", { type: "button", onClick: () => tour.stop('skip'), style: closeBtnStyle, "aria-label": "Close tour", children: "Close" }) }), step.title ? (_jsx("div", { id: titleId, style: titleStyle, children: step.title })) : null, step.description ? _jsx("p", { style: descriptionStyle, children: step.description }) : null, !targetEl && (_jsx("div", { style: missingTargetStyle, role: "note", children: "We're lining things up - this step will resume once the UI is ready." })), _jsx("div", { id: descriptionId, style: { marginBottom: 16 }, children: step.content }), _jsxs("div", { style: { display: 'flex', gap: 8, justifyContent: 'flex-end' }, children: [_jsx("button", { type: "button", onClick: () => tour.stop('skip'), style: btnStyle, children: "Skip" }), _jsx("button", { type: "button", onClick: () => tour.back(), disabled: index <= 0, style: btnStyle, children: "Back" }), _jsx("button", { type: "button", onClick: () => tour.next(), style: primaryBtnStyle, children: "Next" })] })] }) }), previewBadge && (_jsxs("div", { style: {
                    position: 'fixed',
                    top: 8,
                    right: 8,
                    padding: '4px 8px',
                    fontSize: 12,
                    fontWeight: 600,
                    borderRadius: 6,
                    background: 'rgba(255,196,0,0.92)',
                    color: '#111',
                    pointerEvents: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                }, children: ["PREVIEW", _jsx("button", { onClick: () => {
                            try {
                                sessionStorage.removeItem('tour.preview');
                            }
                            catch {
                                /* noop */
                            }
                            // Reload without query params to ensure preview mode is fully cleared
                            const u = new URL(window.location.href);
                            u.searchParams.delete('tourPreviewToken');
                            window.location.replace(u.toString());
                            setPreviewBadge(false);
                        }, style: {
                            appearance: 'none',
                            border: 'none',
                            background: 'transparent',
                            color: '#111',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            fontSize: 12,
                            padding: 0,
                        }, "aria-label": "Exit preview", children: "Exit" })] }))] }));
}
const panelStyle = {
    background: 'var(--tour-bg, #fff)',
    color: 'var(--tour-fg, #111)',
    borderRadius: 'var(--tour-radius, 8px)',
    border: '1px solid var(--tour-border, rgba(0,0,0,0.12))',
    boxShadow: 'var(--tour-shadow, 0 18px 44px rgba(15,23,42,0.35))',
    padding: 16,
    maxHeight: 'min(60vh, 420px)',
    overflowY: 'auto',
    lineHeight: 1.45,
};
const titleStyle = {
    fontSize: 18,
    fontWeight: 600,
    marginBottom: 8,
};
const descriptionStyle = {
    marginBottom: 12,
    color: 'var(--tour-fg-muted, #52525b)',
    fontSize: 14,
};
const missingTargetStyle = {
    marginBottom: 12,
    padding: '8px 12px',
    borderRadius: 6,
    background: 'rgba(15,23,42,0.08)',
    color: 'var(--tour-fg-muted, #4b5563)',
    fontSize: 13,
};
const closeBtnStyle = {
    appearance: 'none',
    border: 'none',
    background: 'transparent',
    color: 'var(--tour-fg-muted, #555)',
    fontSize: 12,
    fontWeight: 600,
    textDecoration: 'underline',
    cursor: 'pointer',
    padding: 4,
};
const btnStyle = {
    appearance: 'none',
    border: '1px solid rgba(0,0,0,0.2)',
    background: 'white',
    color: '#111',
    borderRadius: 6,
    padding: '6px 10px',
    cursor: 'pointer',
};
const primaryBtnStyle = {
    ...btnStyle,
    background: 'var(--tour-accent, #1677ff)',
    color: 'var(--tour-accent-fg, #fff)',
    border: '1px solid var(--tour-accent, #1677ff)',
};
//# sourceMappingURL=Overlay.js.map